import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({ baseURL: BASE_URL });

// Attach JWT on every request
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ── Auto-refresh on 401 ───────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, newToken = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(newToken);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;

    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(newToken => {
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        });
      }

      isRefreshing = true;
      try {
        const { data } = await axios.post(
          `${BASE_URL}/api/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = data.accessToken;
        localStorage.setItem('token', newToken);
        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);

        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);

      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────
export const authApi = {
  signup:              (data) => axios.post(`${BASE_URL}/api/auth/signup`,    data, { withCredentials: true }),
  sendOtp:             (data) => axios.post(`${BASE_URL}/api/auth/send-otp`,   data, { withCredentials: true }),
  verifyOtp:           (data) => axios.post(`${BASE_URL}/api/auth/verify-otp`, data, { withCredentials: true }),
  login:               (data) => axios.post(`${BASE_URL}/api/auth/login`,     data, { withCredentials: true }),
  refresh:             ()     => axios.post(`${BASE_URL}/api/auth/refresh`,   {},   { withCredentials: true }),
  logout:              ()     => axios.post(`${BASE_URL}/api/auth/logout`,    {},   { withCredentials: true }),
  sendEmailUpdateOtp:  (data) => api.post('/api/auth/send-email-update-otp', data),
  updateProfile:       (data) => api.put('/api/auth/profile', data),
};

// ── Categories ────────────────────────────────────────────────────────────
export const categoriesApi = {
  list:             (sort = 'recent', starred = false) => {
    let url = `/api/categories?sort=${encodeURIComponent(sort)}`;
    if (starred) url += '&starred=true';
    return api.get(url);
  },
  create:           (data)            => api.post('/api/categories', data),
  getById:          (id)              => api.get(`/api/categories/${id}`),
  update:           (id, data)        => api.patch(`/api/categories/${id}`, data),
  toggleStar:       (id)              => api.patch(`/api/categories/${id}/star`),
  toggleFavorite:   (id)              => api.patch(`/api/categories/${id}/star`),
  delete:           (id)              => api.delete(`/api/categories/${id}`),
  searchCategories: (data)            => api.post('/api/categories/search', data),
  searchFolders:    (data)            => api.post('/api/categories/search', data),
};

// Alias for backwards compatibility
export const foldersApi = categoriesApi;

// ── Notes ─────────────────────────────────────────────────────────────────
export const notesApi = {
  create:          (data) => api.post('/api/notes', data),
  list:            ({ categoryId, folderId, starred = false, favorite = false, sort = 'recent', page = 0, size = 20 } = {}) => {
    let url = `/api/notes?sort=${encodeURIComponent(sort)}&page=${page}&size=${size}`;
    const targetCat = categoryId != null ? categoryId : folderId;
    if (targetCat != null) url += `&categoryId=${targetCat}`;
    if (starred || favorite) url += `&starred=true`;
    return api.get(url);
  },
  get:             (id) => api.get(`/api/notes/${id}`),
  getById:         (id) => api.get(`/api/notes/${id}`),
  update:          (id, data) => api.patch(`/api/notes/${id}`, data),
  toggleStar:      (id) => api.patch(`/api/notes/${id}/star`),
  toggleFavorite:  (id) => api.patch(`/api/notes/${id}/star`),
  delete:          (id) => api.delete(`/api/notes/${id}`), // moves to trash
  restore:         (id) => api.post(`/api/notes/${id}/restore`),
  permanentDelete: (id) => api.delete(`/api/notes/${id}/permanent`),
  listTrash:       () => api.get('/api/notes/trash'),
  emptyTrash:      () => api.delete('/api/notes/trash'),
  searchNotes:     (data) => api.post('/api/notes/search', data),
  getCleanup:      (params = {}) => {
    if (typeof params === 'number') {
      return api.get(`/api/notes/cleanup?size=${params}`);
    }
    const { page = 0, size = 10, all = false } = params;
    return api.get(`/api/notes/cleanup?page=${page}&size=${size}&all=${all}`);
  },
  history:         (id, page = 0, size = 50) => api.get(`/api/notes/${id}/history?page=${page}&size=${size}`),
};

// Aliases for backward compatibility
export const documentsApi = notesApi;
export const docsApi = {
  ...notesApi,
  leastImportant: (limit = 20) => notesApi.getCleanup(limit),
  search: (data) => notesApi.searchNotes(data),
};

// ── Workspace Summary ─────────────────────────────────────────────────────
export const summaryApi = {
  get: () => api.get('/api/summary'),
};

export default api;
